const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const textFrom = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const normalisePoint = (p) => ({
  bpId:          textFrom(p.bpId, p.id, p.cityPointId),
  id:            textFrom(p.bpId, p.id, p.cityPointId),
  name:          textFrom(p.name, p.cityPointName, p.pointName),
  time:          textFrom(p.time, p.bpTime, p.dpTime),
  address:       textFrom(p.address, p.location),
  landmark:      textFrom(p.landmark, p.landMark),
  prime:         p.prime ?? false,
});

export function adaptApiBus(raw) {

  const parseTime = (dt) => {
    if (!dt) return "--:--";
    return new Date(dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  // ✅ Use raw.ac / raw.sleeper booleans directly from API
  const busTypeLower = (raw.busType || "").toLowerCase();
  const isAC      = raw.ac      ?? (!busTypeLower.includes("non ac") && !busTypeLower.includes("non-ac"));
  const isSleeper = raw.sleeper ?? busTypeLower.includes("sleeper");

  // ✅ Price from fareDetail
  let price = 0;
  if (raw.fareDetail) {
    price = Number(raw.fareDetail.totalFare ?? raw.fareDetail.baseFare ?? 0);
  } else {
    price = Number(raw.minimumFare) || Number(raw.fare) || Number(raw.price) || 0;
  }
  const originalPrice = Number(raw.fareDetail?.originalFare) || price;

  // ✅ Duration — try datetime diff first, then parse "2:58 hrs" string
  const durationMins = (() => {
    if (raw.departureDateTime && raw.arrivalDateTime) {
      const diff = Math.round((new Date(raw.arrivalDateTime) - new Date(raw.departureDateTime)) / 60000);
      if (diff > 0) return diff;
    }
    if (raw.duration) {
      const match = raw.duration.match(/(\d+):(\d+)/);
      if (match) return parseInt(match[1]) * 60 + parseInt(match[2]);
    }
    return 0;
  })();

  const hours    = Math.floor(durationMins / 60);
  const mins     = durationMins % 60;
  const duration = durationMins > 0 ? `${hours}h ${mins}m` : "--";

  // ✅ Departure slot
  const depHour = raw.departureDateTime ? new Date(raw.departureDateTime).getHours() : -1;
  let departureSlot = "after18";
  if      (depHour >= 0  && depHour < 6)  departureSlot = "before6";
  else if (depHour >= 6  && depHour < 12) departureSlot = "6to12";
  else if (depHour >= 12 && depHour < 18) departureSlot = "12to18";

  // ✅ Tags
  const tags = [];
  if (price > 0 && price < 600)           tags.push("budget");
  if (isAC && isSleeper)                  tags.push("luxury");
  if (durationMins > 0 && durationMins < 180) tags.push("fastest");

  // ✅ Policies from cancellationPolicy
  const policies = (raw.cancellationPolicy || []).map(p => ({
    title: p.timeBeforeTravel || "Policy",
    text:  `${p.percentage}% deduction`,
    icon:  "",
  }));

  return {
    id:                raw.id,
    operator:          raw.travels || raw.operatorName || "Unknown Operator",
    busType:           raw.busType || "Standard",
    departureDateTime: raw.departureDateTime,
    arrivalDateTime:   raw.arrivalDateTime,
    departure:         parseTime(raw.departureDateTime),
    arrival:           parseTime(raw.arrivalDateTime),
    duration,
    durationMins,
    departureSlot,
    departureCity:     raw.sourceCity  || raw.fromCity  || raw.sourceName  || "",
    arrivalCity:       raw.destinationCity || raw.toCity || raw.destinationName || "",
    isAC,
    seatType:          isSleeper ? "sleeper" : "seater",
    seatsLeft:         Number(raw.availableSeats)       || 0,
    singleSeats:       Number(raw.availableSingleSeats) || 0,
    price,
    originalPrice,
    rating:            Number(raw.rating) || 3.5,
    totalRatings:      Number(raw.totalRatings) || 0,
    hasGPS:            raw.gpsEnabled || raw.hasGps || false,
    amenities:         Array.isArray(raw.amenities) ? raw.amenities : [],
    tags,
    category:          isSleeper ? "sleeper" : "seater",
    boardingPoints:    (raw.bp  || []).map(normalisePoint),
    droppingPoints:    (raw.dp  || []).map(normalisePoint),
    photos:            [],
    policies,
    reviewsList:       [],
    _raw:              raw,
  };
}